/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { formatCurrency, FormattedMessage, useIntl } from '@futrli/localisation';
import clsx from 'clsx';
import React, { FC, useContext } from 'react';

import { useI18nConstants } from '../../../../report-builder/use-i18n-constants';
import { NumberExplainedMessagesIds } from './number-explained-messages';
import { NumberExplainedResponse } from './types';
import { NumberExplainedByMessageId } from './number-explained-for-message-id';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { UserGridSettingsContext } from '../../../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';
import { forceLocalDate } from '../../../../utils/dates';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

import styles from './number-explained.scss';

interface Props {
  data: (NumberExplainedResponse & { actual?: number; predicted?: number; accountName?: string }) | null;
}

export const NumberExplainedContent: FC<Props> = ({ data }) => {
  const { currency } = useContext(PredictManagementContext);
  const { showDecimals } = useContext(UserGridSettingsContext);
  const { periodType, forecast } = usePredictionsInfoContext();
  const intl = useIntl();
  const { getDatePeriodNameKey } = useI18nConstants();

  if (!data) {
    return <NumberExplainedByMessageId messageId={NumberExplainedMessagesIds.ComingSoon} />;
  }

  const context = data.context ?? undefined;
  const remaining = ((data.predicted || 0) - (data.actual || 0) || 0) / 100;

  return (
    <React.Fragment>
      <div className={clsx(styles.header, 'uiTypeFont14Medium')}>
        <FormattedMessage
          id="predict.month_category"
          defaultMessage="{monthName} {category}:"
          values={{
            category: data.accountName || context?.accountCategory,
            monthName: forecast?.forecast_from_date
              ? intl.formatDate(forceLocalDate(forecast?.forecast_from_date), { month: 'long' })
              : ''
          }}
        />
      </div>

      <div className={styles.content}>
        <ul>
          <li>
            <FormattedMessage
              id="predict.predicted_period_end"
              defaultMessage="predicted {period}-end"
              values={{
                period: getDatePeriodNameKey(periodType || 'MONTHLY')
              }}
            />
            <span className={clsx(styles.value)}>
              {formatCurrency(
                (data.predicted || 0) / 100,
                intl,
                currency || '',
                showDecimals ? 2 : 0,
                showDecimals ? 2 : 0
              )}
            </span>
          </li>
          <li>
            <FormattedMessage id="predict.actuals_to_date" defaultMessage="actuals to date" />
            <span className={clsx(styles.value)}>
              {formatCurrency(
                (data.actual || 0) / 100,
                intl,
                currency || '',
                showDecimals ? 2 : 0,
                showDecimals ? 2 : 0
              )}
            </span>
          </li>
          <li>
            <FormattedMessage id="predict.predicted_remaining" defaultMessage="predicted remaining" />
            <span className={clsx(styles.value)}>
              {remaining > 0 ? '+' : ''}
              {formatCurrency(remaining, intl, currency || '', showDecimals ? 2 : 0, showDecimals ? 2 : 0)}
            </span>
          </li>
        </ul>
      </div>
    </React.Fragment>
  );
};
